/** Driver-facing receipts list with status + rejection reason (Croatian). */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fuel, ChevronDown, ChevronRight, AlertCircle, Camera, Download } from "lucide-react";
import { toast } from "sonner";
import driverApi from "@/lib/driverApi";

const eur = (n) => (n === null || n === undefined || n === "" ? "—" : `€${Number(n).toFixed(2)}`);
const hr = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}.`;
};

const MONTHS_HR = ["Siječanj", "Veljača", "Ožujak", "Travanj", "Svibanj", "Lipanj",
  "Srpanj", "Kolovoz", "Rujan", "Listopad", "Studeni", "Prosinac"];

const monthKey = (iso) => (iso || "").slice(0, 7);
const monthLabel = (key) => {
  const [y, m] = key.split("-");
  return m ? `${MONTHS_HR[Number(m) - 1]} ${y}.` : "—";
};

const STATUS = {
  pending:  { label: "na čekanju", cls: "border-amber-500/40 text-amber-400" },
  approved: { label: "odobreno",   cls: "border-emerald-500/40 text-emerald-400" },
  rejected: { label: "odbijeno",   cls: "border-red-500/40 text-red-400" },
};

export const MyReceipts = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(true);
  const [paidMonths, setPaidMonths] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(null);

  useEffect(() => {
    driverApi.get("/driver/fuel-receipts", { params: { limit: 100 } })
      .then(({ data }) => {
        setRows(data?.rows || []);
        setPaidMonths(data?.paid_months || []);
      })
      .catch(() => {});
  }, []);

  const downloadStatement = async (group) => {
    setPdfBusy(group.key);
    try {
      const { buildFuelStatementPdf } = await import("@/lib/fuelStatementPdf");
      const blob = await buildFuelStatementPdf({
        monthKey: group.key,
        rows: group.rows,
        driverName: group.rows[0]?.driver_name,
        paid: paidMonths.includes(group.key),
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `obracun-gorivo-${group.key}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 30_000);
      toast.success(`Obračun za ${monthLabel(group.key)} preuzet.`);
    } catch {
      toast.error("Preuzimanje obračuna nije uspjelo.");
    } finally {
      setPdfBusy(null);
    }
  };

  if (!rows.length) return null;
  const rejected = rows.filter((r) => r.status === "rejected").length;
  const sorted = [...rows].sort((a, b) => (b.receipt_date || "").localeCompare(a.receipt_date || ""));
  const groups = [];
  sorted.forEach((r) => {
    const key = monthKey(r.receipt_date);
    const g = groups[groups.length - 1];
    if (g && g.key === key) g.rows.push(r);
    else groups.push({ key, rows: [r] });
  });

  return (
    <div className="rounded-2xl border border-white/5 bg-[#0a0a0a] overflow-hidden" data-testid="my-receipts">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-4 hover:bg-white/[0.02] transition text-left"
        data-testid="my-receipts-toggle"
      >
        {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
        <Fuel className="w-4 h-4 text-[#C5A059]" />
        <div className="flex-1 min-w-0">
          <span className="text-sm text-white whitespace-nowrap block">Moji računi</span>
          <span className="text-[10px] text-zinc-500 block">{rows.length} zadnjih</span>
        </div>
        {groups[0] && (
          <span
            className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-[#C5A059] text-black font-bold shrink-0"
            data-testid="my-receipts-month-badge"
          >
            {monthLabel(groups[0].key)}
          </span>
        )}
        {rejected > 0 && (
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-red-500/40 text-red-400"
                data-testid="my-receipts-rejected-count">
            {rejected} odbijeno
          </span>
        )}
      </button>

      {open && (
        <div className="border-t border-white/5 max-h-96 overflow-y-auto">
          <p className="px-4 py-3 text-[11px] leading-relaxed text-zinc-500 border-b border-white/5 bg-white/[0.02]"
             data-testid="my-receipts-info-note">
            Povrat za gorivo i troškove obračunava se <span className="text-zinc-300">jednom mjesečno</span> — isplaćuje
            se za prethodni kalendarski mjesec i <span className="text-zinc-300">neovisan je o tjednim isplatama vožnji</span> prikazanim ispod.
          </p>
          {groups.map((g) => {
            const refund = g.rows
              .filter((r) => r.status === "approved")
              .reduce((s, r) => s + (Number(r.refund_eur) || 0), 0);
            const paid = paidMonths.includes(g.key);
            return (
              <div key={g.key}>
                <div className="flex items-center gap-2 px-4 py-2 bg-[#C5A059]/[0.07] border-y border-[#C5A059]/15"
                     data-testid={`my-receipts-month-${g.key}`}>
                  <span className="text-[11px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold">
                    {monthLabel(g.key)}
                  </span>
                  {paid && (
                    <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 font-semibold"
                          data-testid={`my-receipts-paid-${g.key}`}>
                      Isplaćeno
                    </span>
                  )}
                  <span className="ml-auto text-[11px] text-zinc-400">
                    {g.rows.length} rač. · <span className="text-[#C5A059]">povrat {eur(refund)}</span>
                  </span>
                  <button
                    onClick={() => downloadStatement(g)}
                    disabled={pdfBusy === g.key}
                    className="p-1 rounded-md text-zinc-400 hover:text-[#C5A059] hover:bg-white/5 transition disabled:opacity-50"
                    title="Preuzmi mjesečni obračun (PDF)"
                    data-testid={`my-receipts-pdf-${g.key}`}
                  >
                    <Download className={`w-4 h-4 ${pdfBusy === g.key ? "animate-pulse" : ""}`} />
                  </button>
                </div>
                <div className="divide-y divide-white/5">
                  {g.rows.map((r) => {
            const s = STATUS[r.status] || STATUS.pending;
            return (
              <div key={r.id} className="p-3.5" data-testid={`my-receipt-${r.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-200">
                      {hr(r.receipt_date)} · {r.category_label || "Gorivo"}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {eur(r.amount_eur)}
                      {r.status === "approved" && <span className="text-[#C5A059]"> · povrat {eur(r.refund_eur)}</span>}
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border shrink-0 ${s.cls}`}>
                    {s.label}
                  </span>
                </div>
                {r.status === "rejected" && (
                  <div className="mt-2 p-2.5 rounded-lg border border-red-500/30 bg-red-500/5"
                       data-testid={`my-receipt-reason-${r.id}`}>
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                      <div className="text-xs text-red-300">
                        <span className="font-semibold">Razlog odbijanja: </span>
                        {r.rejection_reason || "Račun nije prihvaćen — pošaljite čitljivu fotografiju ponovno."}
                      </div>
                    </div>
                    <Link
                      to="/driver/dashboard?upload=1"
                      className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#C5A059] hover:text-[#D4AF37] transition"
                      data-testid={`my-receipt-reupload-${r.id}`}
                    >
                      <Camera className="w-3.5 h-3.5" /> Pošalji novi račun
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
